import { Component, Input, Output, EventEmitter, OnInit, ViewChild, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { FiDataService, FIDataResponse } from '../../../../../core/services/fi-data.service';
import { BaseChartDirective } from 'ng2-charts';
import { MOCK_INVESTMENT_DETAIL_DATA, InvestmentDetailData } from './investment-overview-modal.mock';
import {
    Chart as ChartJS,
    ChartConfiguration,
    ChartType,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { ModalComponent } from '../../../../../shared/components/modal/modal';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
);

@Component({
    selector: 'app-investment-overview-modal',
    standalone: true,
    imports: [CommonModule, ModalComponent, BaseChartDirective],
    templateUrl: './investment-overview-modal.html',
    styleUrls: ['./investment-overview-modal.scss']
})
export class InvestmentOverviewModalComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isOpen: boolean = false;
    @Input() assetName: string = 'Apple';
    @Output() close = new EventEmitter<void>();

    @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

    private subscription = new Subscription();
    isLoading = true;
    apiData: InvestmentDetailData = this.cloneMockData();
    activeTab: 'daily' | 'monthly' | 'weekly' | 'annually' = 'monthly';

    private cloneMockData(): InvestmentDetailData {
        return JSON.parse(JSON.stringify(MOCK_INVESTMENT_DETAIL_DATA));
    }


    public barChartData: ChartConfiguration['data'] = {
        datasets: [
            {
                data: [],
                backgroundColor: [],
                borderRadius: 0,
                borderSkipped: false
            }
        ],
        labels: []
    };

    public barChartOptions: ChartConfiguration['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (context: any) => {
                        const dataset = context.dataset;
                        const index = context.dataIndex;
                        const ohlc = (dataset as any).ohlcData;
                        if (ohlc && ohlc[index]) {
                            const [low, high, open, close] = ohlc[index];
                            return [
                                `Open: ${open}`,
                                `High: ${high}`,
                                `Low: ${low}`,
                                `Close: ${close}`
                            ];
                        }
                        return '';
                    }
                }
            }
        },
        layout: {
            padding: {
                top: 30,
                bottom: 0,
                right: 10
            }
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#8E8E93',
                    font: {
                        size: 12,
                        weight: 500
                    }
                }
            },
            y: {
                display: true,
                position: 'right',
                suggestedMax: 100,
                grid: {
                    color: () => getComputedStyle(document.body).getPropertyValue('--chart-grid-color') || 'rgba(0, 0, 0, 0.05)',
                    drawTicks: false
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#8E8E93',
                    font: {
                        size: 12
                    },
                    stepSize: 20
                }
            }
        },
        datasets: {
            bar: {
                barPercentage: 0.3,
                categoryPercentage: 0.9
            }
        }
    };

    public barChartType: ChartType = 'bar';
    public chartPlugins: any[] = [];

    // Custom plugin to draw the high/low lines (wicks/shadows)
    public candlestickPlugin = {
        id: 'candlestickPlugin',
        afterDatasetsDraw: (chart: any) => {
            const { ctx, data, scales: { x, y } } = chart;

            if (!data.datasets || !data.datasets[0] || !data.datasets[0].data) return;

            const meta = chart.getDatasetMeta(0);

            data.datasets[0].data.forEach((datapoint: any, index: number) => {
                const ohlc = (data.datasets[0] as any).ohlcData;
                if (!ohlc || !ohlc[index]) return;

                const [low, high, open, close] = ohlc[index];

                // Get the bar element to find its exact center
                const bar = meta.data[index];
                if (!bar) return;

                const xPos = bar.x;
                const bodyTop = y.getPixelForValue(Math.max(open, close));
                const bodyBottom = y.getPixelForValue(Math.min(open, close));
                const yHigh = y.getPixelForValue(high);
                const yLow = y.getPixelForValue(low);

                ctx.save();
                const color = open <= close ? '#26BC8F' : '#EF4D6C';
                ctx.strokeStyle = color;
                ctx.lineWidth = 2;

                // Upper Shadow (Wick)
                ctx.beginPath();
                ctx.moveTo(xPos, yHigh);
                ctx.lineTo(xPos, bodyTop);
                ctx.stroke();

                // Lower Shadow (Wick)
                ctx.beginPath();
                ctx.moveTo(xPos, yLow);
                ctx.lineTo(xPos, bodyBottom);
                ctx.stroke();

                // Horizontal Caps (Box Plot style)
                const capWidth = 6;
                ctx.beginPath();
                ctx.moveTo(xPos - capWidth, yHigh);
                ctx.lineTo(xPos + capWidth, yHigh);
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(xPos - capWidth, yLow);
                ctx.lineTo(xPos + capWidth, yLow);
                ctx.stroke();

                ctx.restore();
            });
        }
    };

    constructor(
        private fiDataService: FiDataService,
        private cdr: ChangeDetectorRef
    ) {
        this.chartPlugins = [this.candlestickPlugin as any];
    }

    ngOnInit() {
        this.subscription.add(
            this.fiDataService.fiData$.subscribe((response: FIDataResponse) => {
                if (response && response.status === 'success' && Array.isArray(response.data)) {
                    this.loadAssetDetails(response.data);
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes['assetName'] || changes['isOpen']) && this.isOpen) {
            // Reset state so we always show a fresh load for each opened asset
            this.isLoading = true;
            this.apiData = this.cloneMockData();
            this.activeTab = 'monthly';
            this.cdr.detectChanges();

            // Re-fetch or re-apply data when asset changes or modal opens
            const currentData = this.fiDataService.getFiState();
            if (currentData && currentData.data) {
                this.loadAssetDetails(currentData.data);
            }
        }
    }

    private loadAssetDetails(data: any[]) {
        const item = data.find(fi => {
            const summary = fi.Summary || {};
            let name = summary.companyName || summary.issuerName || summary.schemeName || fi.bank || fi.fiType;
            if (fi.fiType === 'MUTUAL_FUNDS') name = `${name} (MF)`;
            else if (fi.fiType === 'ETF') name = `${name} (ETF)`;
            else if (fi.fiType === 'NPS') name = `${fi.bank} (NPS)`;

            return name === this.assetName;
        });

        if (item) {
            const currentVal = parseFloat(item.Summary?.currentValue || item.Summary?.balance || 0);

            this.apiData.assetInfo = {
                name: this.assetName,
                trend: '+1%', // Simulated
                isPositive: true,
                openingBalance: currentVal * 0.98, // Simulated
                currentBalance: currentVal,
                change: `+1% ${(currentVal * 0.01).toLocaleString('en-IN')}`,
                currency: 'INR'
            };

            // Enhance trends based on the real value
            this.generateTrendsForValue(currentVal);
            this.updateChartData();
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    private generateTrendsForValue(val: number) {
        // Base multipliers for trends to avoid static mock data
        const tabs: Array<'daily' | 'weekly' | 'monthly' | 'annually'> = ['daily', 'weekly', 'monthly', 'annually'];

        tabs.forEach(tab => {
            if (this.apiData.trends[tab]) {
                this.apiData.trends[tab].values = this.apiData.trends[tab].values.map(v =>
                    v.map(subV => (val / 100000) * subV) // Scale mock data to actual value magnitude
                );
            }
        });
    }


    updateChartData() {
        const currentTrend = this.apiData.trends[this.activeTab];

        // For the bar chart, we use the open/close values for the bar height
        // Chart.js bar chart can take [start, end] for floating bars
        const datasets = this.barChartData.datasets;
        if (datasets && datasets.length > 0 && datasets[0]) {
            const dataset = datasets[0];
            // Map OHLC to [min, max] for floating bar heights
            // We use Math.min/max to ensure Chart.js draws the bar correctly
            dataset.data = currentTrend.values.map(v => [Math.min(v[2], v[3]), Math.max(v[2], v[3])]);

            // We store the full OHLC data in a custom property for the plugin to use
            (dataset as any).ohlcData = currentTrend.values;

            // Set background colors explicitly based on Open vs Close
            dataset.backgroundColor = currentTrend.values.map(v => v[2] <= v[3] ? '#26BC8F' : '#EF4D6C');
        }

        if (this.activeTab === 'daily') {
            const labels = new Array(currentTrend.values.length).fill('');
            const displayLabels = currentTrend.displayLabels;
            labels[0] = displayLabels[0];
            labels[Math.floor(labels.length / 3)] = displayLabels[1];
            labels[Math.floor(labels.length * 2 / 3)] = displayLabels[2];
            labels[labels.length - 1] = displayLabels[3];
            this.barChartData.labels = labels;
        } else {
            this.barChartData.labels = currentTrend.displayLabels;
        }

        // Defer update so the BaseChartDirective ViewChild is mounted and ready
        setTimeout(() => this.chart?.update(), 0);
    }

    onTabChange(tab: 'daily' | 'monthly' | 'weekly' | 'annually') {
        this.activeTab = tab;
        this.updateChartData();
    }

    onDone() {
        this.close.emit();
    }

    onCancel() {
        this.close.emit();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
